import {
  Component, Input, ElementRef, OnInit, OnDestroy,
  PLATFORM_ID, inject, ChangeDetectorRef
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-counter',
  standalone: true,
  template: `<span>{{ prefix }}{{ displayValue }}{{ suffix }}</span>`,
})
export class CounterComponent implements OnInit, OnDestroy {
  @Input() target = 0;
  @Input() duration = 2000;
  @Input() prefix = '';
  @Input() suffix = '';

  displayValue = '0';
  private observer: IntersectionObserver | null = null;
  private animationId = 0;
  private started = false;
  private el = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.displayValue = this.formatNumber(this.target);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.started) {
          this.started = true;
          this.startCounting();
          this.observer?.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (isPlatformBrowser(this.platformId)) {
      cancelAnimationFrame(this.animationId);
    }
  }

  private startCounting(): void {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / this.duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const current = Math.round(eased * this.target);
      this.displayValue = this.formatNumber(current);
      this.cdr.detectChanges();

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      }
    };
    this.animationId = requestAnimationFrame(animate);
  }

  private formatNumber(n: number): string {
    return n.toLocaleString('en-US');
  }
}
